<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-stock-config">
        <i class="far fa-inventory"></i> Stock Config
      </Tab>
      <Tab tabid="tab-settings">
        <i class="far fa-cogs"></i> Settings
      </Tab>
      <Tab tabid="tab-activity-log">
        <i class="far fa-history"></i> Activity Log
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-stock-config">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OptimiserPermissionTypes.optimisationConfig) && !stockConfig.editing" class="float-right">
              <DropdownMenuItem @click="stockConfig.editing = true" icon="fa-pencil">Edit Stock Config</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Stock Config</ContentTitle>
            <ContentSubTitle>Optimiser configuration for stock based on sheet thickness and types. Settings can be overridden per stock via stock page</ContentSubTitle>
          </div>
        </div>
        <Spinner v-if="loading" />
        <FormGroup :labelColumnSize="6" :form="stockConfig.form" v-show="!loading">
          <div class="row mt-2">
            <div class="col-12">
              <OptimiserStockConfigEditor :editing="stockConfig.editing" v-model="stockConfig.list" />
            </div>
          </div>

          <template v-if="stockConfig.editing">
            <FormHorizontalRule />
            <FormSubmit label="Update" :submitting="stockConfig.submitting" @submitted="submitStockConfigSettings" @cancelled="cancelStockConfigSettings" />
          </template>
        </FormGroup>        
      </TabPage>

      <TabPage tabid="tab-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OptimiserPermissionTypes.optimisationConfig) && !generalConfig.editing" class="float-right">
              <DropdownMenuItem @click="generalConfig.editing = true" icon="fa-pencil">Edit Optimiser Config</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Optimiser Settings</ContentTitle>
            <ContentSubTitle>General settings for the optimiser system. For station specific settings, access the optimiser production station page</ContentSubTitle>
          </div>
        </div>
        <Spinner v-if="loading" />
        <FormGroup :labelColumnSize="6" :form="generalConfig.form" v-show="!loading">
          <div class="row mt-2">
            <div class="col-6">

              <FormAccountSetting label="V Cuts - Enabled" helpDefault="True" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value will cause the optimiser to automatically process internal V shaped cuts and flag them for cutting at optimisation rather than at waterjet / CNC based processes.<br />
                  Internal V shaped cutouts are simple 2 lined cutouts with the max depth / max angle rules below.
                  </p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.optimiserVCutEnabled)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.optimiserVCutEnabled)" @change="setConfigSetting(AccountSettingKeys.optimiserVCutEnabled, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="V Cuts - Max Depth" helpDefault="15mm" class="mt-4">
                <template v-slot:helpContent>
                  <p>Maximum depth away from the edge of the V cut to be considered for processing by the optimiser station.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.optimiserVCutMaxDepth)}} mm</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.optimiserVCutMaxDepth)" @input="setConfigSetting(AccountSettingKeys.optimiserVCutMaxDepth, $event)" addon="mm" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="V Cuts - Min Angle" helpDefault="45 Degrees" class="mt-4">
                <template v-slot:helpContent>
                  <p>Minimum angle of the V cut to be considered for processing by the optimiser station.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.optimiserVCutMinAngle)}} degrees</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.optimiserVCutMinAngle)" @input="setConfigSetting(AccountSettingKeys.optimiserVCutMinAngle, $event)" addon="deg" />
                </template>
              </FormAccountSetting>

            </div>
          </div>
          <FormSubmit v-if="generalConfig.editing" label="Update" :submitting="generalConfig.submitting" @submitted="submitGeneralConfigSettings" @cancelled="cancelGeneralConfigSettings" />
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-activity-log">
        <div class="row">
          <div class="col-lg-12">
            <ActivityLogStream ref="activityStream" url="/optimiser/config/activitystream">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import Utils from '@/utils/utils'
import form from '@/mixins/form'
import OptimiserStockConfigEditor from '@/modules/optimiser/components/optimiserStockConfigEditor'

import { InventoryStockTypes, OrderEditorCutoutTypes, OrderEditorCutoutTypeLookup, OrderEditorGeometryShapeTypeLookup, AccountSettingKeys } from '@/store/types'

export default {
  name: 'OptimiserConfiguration',
  mixins: [form],
  components: {
    OptimiserStockConfigEditor
  },
  data: () => {
    return {
      loading: false,
      loadingError: null,
      stockConfig: {
        editing: false,
        submitting: false,
        form: {},
        list: []
      },
      generalConfig: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      },
    }
  },
  created() {
    this.AccountSettingKeys = AccountSettingKeys    
  },
  mounted() {
    this.requestData()
  },
  computed: {
    
  },
  methods: {
    reloadData() {
      this.reloadActivity()
      this.requestData()
    },
    reloadActivity() {
      this.$refs.activityStream.reloadData()
    },
    requestData() {
      this.loadingError = false
      this.loading = true
      Promise.all([
        this.$apiService.optimiserService.getOptimiserStockConfigList(),
        this.$apiService.optimiserService.getConfigSettings(),
      ])
      .then(results => {
        this.$data.stockConfig.list = results[0].data
        this.$data.generalConfig.settings = results[1].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })
    },

    submitStockConfigSettings() {
      this.stockConfig.submitting = true

      let updateData = {
        optimiserStockConfigs: this.stockConfig.list.map(stockConfig => {
          var outputData = _.cloneDeep(stockConfig)
          if(stockConfig.isNew)
            delete outputData.id
          return outputData
        })
      }
      this.$apiService.optimiserService.updateOptimiserStockConfigList(updateData)
      .then(response => {
        this.stockConfig.editing = false
        this.successToast('Editor Edgework List Updated')
      })
      .catch(err => {
        this.stockConfig.form = this.processFormResponse(err)        
      })
      .finally(() => {
        this.stockConfig.submitting = false
      })
    },
    cancelStockConfigSettings() {
      this.stockConfig.editing = false
      this.stockConfig.submitting = false
      this.reloadData()
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.generalConfig.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseBoolean(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return parseInt(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseDecimal(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.generalConfig.settings[settingKey] = value
    },

    submitGeneralConfigSettings() {
      this.$data.generalConfig.submitting = true
      this.$apiService.optimiserService.updateConfigSettings({ settings: this.$data.generalConfig.settings })
      .then(response => {
        this.$data.generalConfig.editing = false
        this.successToast('General Config Settings updated')
      })
      .catch(err => {
        this.$data.generalConfig.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.generalConfig.submitting = false
      })
    },
    cancelGeneralConfigSettings() {
      this.$data.generalConfig.editing = false
      this.$apiService.optimiserService.getConfigSettings()
      .then(response => {
        this.$data.generalConfig.settings = response.data
      })
    },
  }
}

</script>